import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Switch } from "../../components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

// Admin switch for mandatory-document enforcement (this operator only). OFF = read-only assessment.
export const ComplianceEnforcementSwitch = ({ onChange }) => {
  const [st, setSt] = useState(null);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setSt((await api.get("/admin/compliance/enforcement")).data); } catch { setSt(null); }
  };
  useEffect(() => { load(); }, []);

  const confirm = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/compliance/enforcement", { enabled: pending });
      setSt(res.data);
      toast.success(pending ? "Compliance enforcement is ON" : "Compliance enforcement is OFF (assessment only)");
      onChange?.(res.data.enabled);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change the switch");
    } finally {
      setSaving(false);
      setPending(null);
    }
  };

  if (!st) return null;
  const on = st.enabled;
  return (
    <div className={`border p-4 ${on ? "bg-[#FEF2F2] border-[#FCA5A5]" : "bg-[#F9FAFB] border-border"}`} data-testid="compliance-enforcement-card">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="font-heading font-black text-base">
            Compliance enforcement:{" "}
            <span className={on ? "text-[#B91C1C]" : "text-muted-foreground"} data-testid="compliance-enforcement-state">{on ? "ON" : "OFF"}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 max-w-xl">
            {on
              ? "Drivers with missing, expired or rejected mandatory documents can't go online, get offers or accept jobs, and receive one Compliance Hold email."
              : "Assessment only — nobody is blocked, taken offline or emailed. The report below shows who WOULD be blocked."}
          </div>
          {st.updated_at && (
            <div className="text-[11px] text-muted-foreground mt-1" data-testid="compliance-enforcement-updated">
              Last changed {st.updated_at.slice(0, 16).replace("T", " ")} UTC by {st.updated_by_email}
            </div>
          )}
        </div>
        <Switch checked={on} onCheckedChange={(v) => setPending(v)} disabled={saving} data-testid="compliance-enforcement-switch" />
      </div>
      {st.audit?.length > 0 && (
        <details className="mt-2 text-[11px]" data-testid="compliance-enforcement-audit">
          <summary className="cursor-pointer text-muted-foreground">Change log ({st.audit.length})</summary>
          <ul className="mt-1 space-y-0.5 font-mono-tab">
            {st.audit.map((a) => (
              <li key={a.id} data-testid={`compliance-enforcement-audit-${a.id}`}>
                {a.at.slice(0, 16).replace("T", " ")} · {a.admin_email} · {a.previous ? "ON" : "OFF"} → {a.new ? "ON" : "OFF"}
              </li>
            ))}
          </ul>
        </details>
      )}
      <AlertDialog open={pending !== null} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="compliance-enforcement-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{pending ? "Turn compliance enforcement ON?" : "Turn compliance enforcement OFF?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {pending
                ? "Non-compliant drivers will be blocked straight away, online ones taken offline, and each gets one Compliance Hold email."
                : "New document blocks stop. Nobody is put back online automatically, and approval or suspension rules still apply."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="compliance-enforcement-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirm} data-testid="compliance-enforcement-confirm-btn">
              {pending ? "Turn ON" : "Turn OFF"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ComplianceEnforcementSwitch;
